import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiFetch, ApiError } from '../lib/api'
import { useAuthStore } from '../store/auth'
import type { ChurchInvite } from '@fellowship/shared'
import { Wordmark } from '../components/ui/Wordmark'

interface InviteData {
  invite: ChurchInvite
  churchName: string
}

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { accessToken, user } = useAuthStore()

  const [inviteData, setInviteData] = useState<InviteData | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) return
    apiFetch<{ data: InviteData }>(`/invites/${token}`)
      .then((res) => setInviteData(res.data))
      .catch(() => setError('This invite link is invalid or has expired.'))
      .finally(() => setLoading(false))
  }, [token])

  async function handleAccept() {
    if (!token) return
    setAccepting(true)
    setError(null)
    try {
      await apiFetch(`/invites/${token}/accept`, { method: 'POST' })
      setDone(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 py-10">
        <div className="text-[13px] text-muted">Loading invite…</div>
      </div>
    )
  }

  if (error && !inviteData) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 py-10">
        <div className="bg-surface border border-line-soft rounded-[20px] p-9 max-w-[440px] w-full text-center">
          <div className="w-[52px] h-[52px] rounded-full mx-auto mb-4 bg-[#FAEBE7] flex items-center justify-center text-[22px]">✕</div>
          <div className="text-lg font-bold text-[#7A2A1E] mb-2">Invite unavailable</div>
          <div className="text-[13px] text-muted">{error}</div>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 py-10">
        <div className="bg-surface border border-line-soft rounded-[20px] p-9 max-w-[440px] w-full text-center">
          <div className="w-[52px] h-[52px] rounded-full mx-auto mb-4 bg-[#EEF6ED] flex items-center justify-center text-[22px]">✓</div>
          <div className="serif text-[32px] tracking-tight mb-2">You're in!</div>
          <div className="text-sm text-ink-2 mb-6">
            You've joined <strong>{inviteData?.churchName}</strong> as a{' '}
            <strong>{inviteData?.invite.role}</strong>.
          </div>
          <button
            onClick={() => navigate('/')}
            className="btn btn--full btn--lg"
          >
            Go to Fellowship
          </button>
        </div>
      </div>
    )
  }

  const roleLabel = inviteData?.invite.role === 'coordinator' ? 'Coordinator' : 'Viewer'

  return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 py-10">
      <div className="mb-7">
        <Wordmark size={22} />
      </div>

      <div className="bg-surface border border-line-soft rounded-[20px] p-9 max-w-[440px] w-full">
        <h1 className="serif m-0 mb-2 text-[32px] tracking-tight">You've been invited</h1>
        <p className="m-0 mb-6 text-sm text-ink-2 leading-[1.5]">
          You've been invited to help manage{' '}
          <strong className="text-ink">{inviteData?.churchName}</strong> on Fellowship as
          a <strong className="text-ink">{roleLabel}</strong>.
        </p>

        {error && (
          <div role="alert" className="px-3.5 py-3 rounded-[10px] mb-4 bg-[#FAEBE7] border border-[#E9C8C0] text-[#7A2A1E] text-[13px]">
            {error}
          </div>
        )}

        {!accessToken ? (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-[13px] text-muted">
              You need to be signed in to accept this invite.
            </p>
            <button
              onClick={() => navigate(`/login?next=/invites/${token}`)}
              className="btn btn--full btn--lg"
            >
              Sign in to accept
            </button>
            <button
              onClick={() => navigate(`/register?next=/invites/${token}`)}
              className="btn btn--ghost btn--full btn--lg"
            >
              Create account and accept
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="text-[13px] text-muted">
              Accepting as <strong className="text-ink">{user?.email}</strong>
            </div>
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="btn btn--full btn--lg"
            >
              {accepting ? 'Accepting…' : 'Accept invitation'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
